// `import type` uniquement : aucune dépendance runtime vers le Worker.
// C'est l'exception documentée dans docs/ARCHITECTURE.md §2.
import type { AppType } from '@myog/api';
import { hc } from 'hono/client';

/**
 * Client RPC typé de bout en bout.
 *
 * Il n'y a AUCUNE génération de code : les types viennent directement de
 * l'implémentation de l'API. Si une route change de forme, ce fichier ne
 * change pas, mais tous ses appelants cassent au build.
 *
 * Voir docs/adr/003-typage-bout-en-bout.md
 */

/**
 * Origine de l'API. Exportée parce que tout ne passe pas par le client RPC :
 * l'authentification (lib/auth-client.ts) et les téléchargements directs
 * (export RGPD) visent le Worker sans passer par Hono.
 *
 * Une URL relative pointerait sur le dashboard, servi par Cloudflare Pages,
 * qui ne connaît aucune de ces routes.
 */
export const API_URL: string = import.meta.env.VITE_API_URL ?? 'https://api.myog.dev';

export const api = hc<AppType>(API_URL, {
  init: {
    // Les sessions du dashboard passent par cookie httpOnly, jamais par
    // un token stocké en localStorage.
    credentials: 'include',
  },
});

export type ApiClient = typeof api;

/**
 * Traduit une réponse en échec en `Error` porteuse du message de l'API.
 *
 * Chaque route renvoie un `ApiError` structuré — code, message lisible, lien
 * vers la documentation, identifiant de requête. Jeter ce corps pour lui
 * substituer un « Opération impossible » générique revient à s'interdire tout
 * diagnostic depuis l'interface, et à obliger l'utilisateur comme nous à
 * ouvrir l'onglet réseau.
 *
 * Le `requestId` est conservé dans le message : c'est la seule chose qui
 * permet de retrouver la requête correspondante dans les logs du Worker.
 */
export async function apiFailure(response: Response, fallback: string): Promise<Error> {
  try {
    const body = (await response.json()) as {
      error?: { message?: string; code?: string; requestId?: string };
    };

    const message = body.error?.message;
    if (message === undefined) return new Error(`${fallback} (HTTP ${response.status})`);

    const requestId = body.error?.requestId;
    return new Error(requestId === undefined ? message : `${message} (requête ${requestId})`);
  } catch {
    // Réponse non-JSON : une page d'erreur Cloudflare, ou le HTML de Pages si
    // l'URL de l'API est mal configurée.
    return new Error(`${fallback} (HTTP ${response.status})`);
  }
}
